import React, { useContext, useEffect, useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import { MapPin } from "lucide-react";
import PaginationLayout from "../components/PaginationLayout";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import henceofrthEnums from "../utils/henceofrthEnums";
import AgGridTable from "../components/ui/AgGridTable";

interface driverDetail {
  name: string;
}

interface ridebook {
  city_from: string;
  booking_status: string;
  city_to: string;
  total_price: number;
  booked_seats: number;
  driver_detail: driverDetail;
  id: string;
  currency?: any;
}

function RideBooked({ _id }: any) {
  const [page, setPage] = useState<number>(1);
  const [state, setState] = useState({
    data: {
      data: [] as Array<ridebook>,
      total_count: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const limit = 10;
  const { authState } = useContext(GlobalContext);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const onChangePagination = (newVal: number) => {
    setPage(Number(newVal));
  };

  const bookingDetails = async () => {
    setLoading(true);
    try {
      const bookDetail = await henceforthApi.User.pagination(
        _id,
        page - 1,
        limit
      );
      setState(bookDetail);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bookingDetails();
  }, [page]);

  const formatCurrency = (amount: any, currency: any) => {
    if (!amount) return "Not available";
    const formatted = amount;
    if (currency === henceofrthEnums.CurrencyType.NGN) return `₦ ${formatted}`;
    if (currency === henceofrthEnums.CurrencyType.INR) return `₹ ${formatted}`;
    return `$ ${formatted}`;
  };

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => {
          return (page - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Passenger",
        field: "driver_detail.name",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const name = p.data?.driver_detail?.name || "Not available";
          return <span className="font-medium text-slate-800">{name}</span>;
        },
      },
      {
        headerName: "Pick up Location",
        field: "city_to",
        minWidth: 180,
        flex: 1.2,
        cellRenderer: (p: any) => {
          const val = p.value || p.data?.address_from || "Not available";
          return (
            <div className="flex items-center gap-1.5 text-slate-700 truncate text-sm" title={val}>
              <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="truncate">{val}</span>
            </div>
          );
        },
      },
      {
        headerName: "Drop off Location",
        field: "city_from",
        minWidth: 180,
        flex: 1.2,
        cellRenderer: (p: any) => {
          const val = p.value || p.data?.address_to || "Not available";
          return (
            <div className="flex items-center gap-1.5 text-slate-700 truncate text-sm" title={val}>
              <MapPin className="w-4 h-4 text-rose-500 shrink-0" />
              <span className="truncate">{val}</span>
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "booking_status",
        minWidth: 130,
        width: 140,
        cellRenderer: (p: any) => {
          const status = p.value;
          if (!status) return <span className="text-slate-400">Not available</span>;

          const isCompleted =
            status === "COMPLETED" ||
            status === "CONFIRMED" ||
            status === "ACCEPTED";
          const isPending = status === "PENDING" || status === "ONGOING";
          const isCancelled =
            status.startsWith("CANCELLED") || status === "DECLINED";

          let badgeClass = "bg-slate-200 text-slate-700";
          if (isCompleted) badgeClass = "bg-[#0f766e] text-white";
          else if (isPending) badgeClass = "bg-[#d97706] text-white";
          else if (isCancelled) badgeClass = "bg-[#dc2626] text-white";

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${badgeClass}`}
            >
              {status.replace(/_/g, " ")}
            </span>
          );
        },
      },
      {
        headerName: "Booked Seats",
        field: "booked_seats",
        minWidth: 110,
        width: 120,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-700 font-medium">
              {p.value !== undefined && p.value !== null ? p.value : "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Price",
        field: "total_price",
        minWidth: 110,
        width: 130,
        cellRenderer: (p: any) => {
          return (
            <span className="font-bold text-slate-900 text-sm">
              {formatCurrency(p.value, p.data?.currency)}
            </span>
          );
        },
      },
    ];
  }, [page]);

  return (
    <>
      <AgGridTable
        rowData={state?.data?.data || []}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No booked rides found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={state?.data?.total_count || 0}
          data={state?.data?.data || []}
          page={page}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => onChangePagination(val)}
        />
      </div>
    </>
  );
}

export default RideBooked;
